import * as React from 'react';
import {useEffect, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {Add, Delete, Star} from '@mui/icons-material';
import PageHeader from '../layout/PageHeader';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';
import paymentDomains from '../../models/payment-domains';
import useRootHook from '../../hooks/useRoot';

export default function PaymentDomains() {
    const {setShowLoader, notify, showConfirm} = useRootHook();
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [host, setHost] = useState('');
    const [error, setError] = useState(null);

    const load = () => {
        setError(null);
        setShowLoader(true);
        paymentDomains.list()
            .then((data) => {
                setShowLoader(false);
                setRows(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                setShowLoader(false);
                setError(e?.message || 'Failed to load payment domains');
                setRows([]);
            });
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openAdd = () => {
        setEditRow(null);
        setHost('');
        setOpen(true);
    };

    const openEdit = (row) => {
        setEditRow(row);
        setHost(row.host || '');
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        setEditRow(null);
        setHost('');
    };

    const submit = async () => {
        const value = String(host || '').trim();
        if (!value) return notify('Host is required', 2500);
        setShowLoader(true);
        try {
            if (editRow) {
                await paymentDomains.edit({id: editRow.id, host: value});
                notify('Domain updated', 1500);
            } else {
                await paymentDomains.add({host: value});
                notify('Domain added', 1500);
            }
            close();
            load();
        } finally {
            setShowLoader(false);
        }
    };

    const setActive = async (row) => {
        setShowLoader(true);
        try {
            await paymentDomains.setActive(row.id);
            notify('Active domain updated', 1500);
            load();
        } finally {
            setShowLoader(false);
        }
    };

    const remove = async (row) => {
        const confirmed = await showConfirm('Disable this domain?', 'Disable Domain');
        if (!confirmed) return;
        setShowLoader(true);
        try {
            await paymentDomains.delete(row.id);
            notify('Domain disabled', 1500);
            load();
        } finally {
            setShowLoader(false);
        }
    };

    if (!rows) return null;

    const active = rows.find((r) => r.active && r.enabled);

    return (
        <React.Fragment>
            <Grid size={12}>
                <PageHeader
                    title="Payment domains"
                    subtitle="Manage the host used in generated payment links (runtime, no redeploy)"
                    breadcrumbs={[['Dashboard', '/'], ['Settings', '/payment-domains'], ['Payment domains', '/payment-domains']]}
                    actions={
                        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
                            Add domain
                        </Button>
                    }
                />
            </Grid>

            <Grid size={12}>
                <SectionCard sx={{ overflowX: 'auto' }}>
                    <TableToolbar
                        title="Domains"
                        actions={
                            <Typography variant="caption" color="text.secondary">
                                Active: {active?.host ? `https://${active.host}` : 'not set'}
                            </Typography>
                        }
                    />
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Host</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                        {r.host}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {r.active && r.enabled ? (
                                                <Chip size="small" label="Active" color="primary" />
                                            ) : r.enabled ? (
                                                <Chip size="small" label="Enabled" />
                                            ) : (
                                                <Chip size="small" label="Disabled" variant="outlined" />
                                            )}
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Set active">
                                            <span>
                                                <IconButton
                                                    onClick={() => setActive(r)}
                                                    disabled={!r.enabled}
                                                >
                                                    <Star fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton onClick={() => openEdit(r)}>
                                                <Typography variant="body2" sx={{ fontWeight: 800 }}>Edit</Typography>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Disable">
                                            <IconButton onClick={() => remove(r)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            No domains yet. Add one and mark it active.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </SectionCard>
            </Grid>

            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{editRow ? 'Edit domain' : 'Add domain'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Host"
                            placeholder="expaye.top"
                            value={host}
                            onChange={(e) => setHost(e.target.value)}
                            helperText="Hostname only (no https://). Payment links will use https://HOST/payment?... "
                            autoFocus
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" onClick={submit}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}



